import { useState, useEffect } from "react";

import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPagination,
  useGridApiContext,
  useGridSelector,
  gridPaginationModelSelector,
  gridRowCountSelector,
} from "@mui/x-data-grid";
import { SurveyConfiguration } from "@rubi/api-generated";
import { useNavigate, useParams } from "react-router-dom";

import style from "@/components/online-reporting/ReportV2List.module.scss";
import {
  fromGridSortModel,
  SubmissionSort,
  sortableDateColumn,
  toGridSortModel,
} from "@/components/online-reporting/submissionSort";
import { reportColourTypes, statusTypes } from "@/types/typesReportV2";

import { useRegistry } from "./context/RegistryContext";

const createColumns = (fields: GridColDef[]) => {
  return fields.map((f: GridColDef) => ({
    sortable: false,
    ...f,
  }));
};

function CustomPagination() {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageCount = Math.max(
    1,
    Math.ceil(rowCount / (paginationModel.pageSize || 1)),
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
      <Typography variant="body2" sx={{ mr: 1 }}>
        Page:
      </Typography>
      <Select
        size="small"
        value={paginationModel.page}
        onChange={(e) => apiRef.current.setPage(Number(e.target.value))}
        sx={{ minWidth: 60 }}
      >
        {Array.from({ length: pageCount }, (_, i) => (
          <MenuItem key={i} value={i}>
            {i + 1}
          </MenuItem>
        ))}
      </Select>
      <GridPagination />
    </Box>
  );
}

const generateRowData = (
  submission: any,
  registryForms: SurveyConfiguration[],
) => ({
  ...submission,
  time_updated: new Date(submission.time_updated),
  time_created: new Date(submission.time_created),
  time_submitted: submission.time_submitted
    ? new Date(submission.time_submitted)
    : new Date(submission.time_created),
  tracking_number: `${submission.tracking_number ?? ""} v${submission.latest_version}`,
  registry_title: registryForms
    ? registryForms.find(
        (form) => form.survey_class === submission.survey_class,
      )?.name
    : "",
});

const RegistryListView = ({
  reports,
  loadingEntries,
  totalReportsCount,
  paginationModel,
  handlePaginationChange,
  sort,
  onSortChange,
}: {
  reports: any[];
  loadingEntries: boolean;
  totalReportsCount: number;
  paginationModel: { page: number; pageSize: number };
  handlePaginationChange: (params: { page: number; pageSize: number }) => void;
  sort: SubmissionSort;
  onSortChange: (sort: SubmissionSort) => void;
}) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { registryForms, loading: registryFormsLoading } = useRegistry();

  const fields: GridColDef[] = [
    {
      field: "status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        return (
          <Chip
            style={{
              opacity:
                params.value === "rejected" || params.value === "approved"
                  ? 0.6
                  : 1,
            }}
            label={
              statusTypes[params.value]
                ? statusTypes[params.value]
                : params.value
            }
            color={
              reportColourTypes[params.value]
                ? reportColourTypes[params.value]
                : "default"
            }
          />
        );
      },
    },
    {
      field: "assigned_to",
      headerName: "Assigned To",
      width: 200,
      renderCell: (params) => {
        if (!params.value) {
          return <span style={{ color: "#999" }}>Unassigned</span>;
        }
        return (
          <span>
            {params.value.first_name} {params.value.last_name}
            {params.value.badge_number && (
              <span style={{ color: "#666", fontSize: "0.8em" }}>
                {" "}
                ({params.value.badge_number})
              </span>
            )}
          </span>
        );
      },
    },
    {
      field: "incident_number",
      headerName: "RMS Number",
      width: 150,
    },
    {
      field: "tracking_number",
      headerName: "Tracking Number",
      width: 150,
    },
    {
      field: "registry_title",
      headerName: "Registry Title",
      width: 200,
    },
    {
      field: "first_name",
      headerName: "First Name",
      width: 150,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      width: 150,
    },
    {
      field: "time_updated",
      headerName: "Time Updated",
      width: 200,
      type: "dateTime",
      ...sortableDateColumn,
    },
    {
      field: "time_submitted",
      headerName: "Time Submitted",
      width: 200,
      type: "dateTime",
      ...sortableDateColumn,
    },
  ];

  const columns = createColumns(fields);

  // Force re-generation of rows when registryForms becomes available
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    setRows(reports.map((report) => generateRowData(report, registryForms)));
  }, [reports, registryForms]);

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      autoPageSize
      slots={{ pagination: CustomPagination }}
      rowCount={totalReportsCount}
      onPaginationModelChange={(params) => {
        handlePaginationChange(params);
      }}
      initialState={{
        pagination: {
          paginationModel: {
            ...paginationModel,
            pageSize: Math.min(paginationModel.pageSize, 25),
          },
        },
      }}
      paginationMode="server"
      sortingMode="server"
      sortModel={toGridSortModel(sort)}
      onSortModelChange={(model) => onSortChange(fromGridSortModel(model))}
      paginationModel={{
        ...paginationModel,
        pageSize: Math.min(paginationModel.pageSize, 25),
      }}
      rowSelection={false}
      sx={{
        "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
          {
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "inherit",
            display: "flex",
            alignItems: "center",
          },
      }}
      loading={loadingEntries || registryFormsLoading}
      onRowClick={(row) => {
        navigate(`/rubiregistry/${orgId}/${row.row.survey_class}/${row.id}`);
      }}
      getRowClassName={() => style.reportRow}
    />
  );
};

export default RegistryListView;
